import { useState } from 'react';
import { useCentralizedRequests, useCrGates, useSetCrGate } from '../lib/api';
import StatusBadge from '../components/StatusBadge.jsx';

// Status scope toggle. "Held" (default) = the parked host-intake holds awaiting a
// human; "All" = every Centralized-Request row, held or not. Server-side filter —
// see /api/centralized-requests.
const STATUS_OPTIONS = [
  { value: 'held', label: 'Held' },
  { value: 'all',  label: 'All' },
];

// The incomingRequestId is how staff find the held CR back in Sentinel, so make
// it monospace + one-click copyable.
function CopyableId({ id }) {
  const [copied, setCopied] = useState(false);
  if (id == null || id === '') return <span className="text-slate-400">—</span>;
  async function copy() {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) { /* clipboard blocked (insecure context) — non-fatal */ }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy incoming request ID — find it in Sentinel"
      className="font-mono text-xs text-slate-700 hover:text-tint inline-flex items-center gap-1"
    >
      <span>{id}</span>
      <span className="text-[10px] text-slate-400">{copied ? '✓ copied' : '⧉'}</span>
    </button>
  );
}

// The Sentinel Quote of a held CR + a deep link to its operator review page, so
// staff can jump straight into Sentinel to assess the held quote. Shows the
// operator (which fan-out this quote belongs to) + Q#. Plain Q# when there is no
// quote-group GUID (no deep link); '—' for pre-quote intake holds (no quote yet).
function SentinelQuote({ operator, id, url }) {
  if (id == null || id === '') return <span className="text-slate-400">—</span>;
  const op = operator && operator !== 'eas'
    ? <span className="text-slate-500">{operator} · </span>
    : null;
  const quote = url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open this quote in Sentinel to review / assess"
      className="text-tint hover:underline"
    >
      Q#{id}<span className="text-[10px] ml-0.5">↗</span>
    </a>
  ) : (
    <span className="text-slate-700">Q#{id}</span>
  );
  return <span className="font-mono text-xs inline-flex items-center">{op}{quote}</span>;
}

// Accessible on/off switch with tint styling (no switch component exists
// in this codebase, so this is a self-contained button[role=switch]).
function GateToggle({ label, hint, enabled, busy, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-semibold w-7 text-right ${enabled ? 'text-emerald-700' : 'text-slate-400'}`}>
          {enabled ? 'ON' : 'OFF'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={label}
          disabled={busy}
          onClick={() => onToggle(!enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? 'bg-tint' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

// CR processing gates — two independent runtime switches: the global host-intake
// "Save CR" gate, and a per-operator "Quote CR" gate. Each toggle = one POST.
function CrGatesControl() {
  const { data, isLoading, isError, error } = useCrGates();
  const setGate = useSetCrGate();
  const [pending, setPending] = useState(null); // which toggle is mid-flight

  const saveEnabled = !!data?.cr_save_enabled;
  const operators = data?.operators || [];

  const flip = (key, payload) => {
    setPending(key);
    setGate.mutate(payload, { onSettled: () => setPending(null) });
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="font-semibold text-sm text-sentinel-navy">CR processing gates</div>
        {isLoading && <span className="text-xs text-slate-400">…</span>}
      </div>
      <p className="text-xs text-slate-500 mb-2">
        Independent runtime switches for the Centralized-Request pipeline.{' '}
        <span className="font-medium">Save CR</span> is the global host-intake gate;{' '}
        <span className="font-medium">Quote CR</span> lets each operator&apos;s poller pick up and
        quote held CRs. They are independent — Save can be ON while an operator&apos;s Quote is OFF.
      </p>

      {isError && <div className="text-xs text-rose-700 mb-2">{error.message}</div>}

      <div className="divide-y divide-slate-100">
        <GateToggle
          label="Save CR (host intake)"
          hint="Global gate — when ON the intake agent saves incoming Centralized Requests."
          enabled={saveEnabled}
          busy={pending === 'save'}
          onToggle={(next) => flip('save', { gate: 'save', enabled: next })}
        />
        {operators.map((op) => (
          <GateToggle
            key={op.operator}
            label={`Quote CR — ${op.operator}`}
            hint={`When ON, ${op.operator}'s poller may quote held Centralized Requests.`}
            enabled={!!op.cr_quote_enabled}
            busy={pending === `quote:${op.operator}`}
            onToggle={(next) =>
              flip(`quote:${op.operator}`, { gate: 'quote', operator: op.operator, enabled: next })
            }
          />
        ))}
      </div>

      {setGate.isError && <div className="text-xs text-rose-700 mt-2">{setGate.error?.message}</div>}
    </div>
  );
}

function Customer({ name, email }) {
  if (!name && !email) return <span className="text-slate-400">—</span>;
  return (
    <div>
      {name && <div className="text-slate-700">{name}</div>}
      {email && <div className="text-[11px] text-slate-500 break-all">{email}</div>}
    </div>
  );
}

export default function CentralizedRequests() {
  const [status, setStatus] = useState('held');
  const { data, isLoading, isError, error } = useCentralizedRequests({ status });
  const requests = data?.requests || [];

  return (
    <div className="space-y-4">
      {/* Header / explainer */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-semibold text-sentinel-navy">Centralized Requests</h1>
            <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
              Incoming Sentinel Centralized Requests the host intake agent parked for
              human review — unresolvable airports, no route, past-date, and other
              cases it couldn&apos;t auto-create. These don&apos;t appear in Today&apos;s
              Trips (host context, no operator). Open the request in Sentinel by its
              incoming-request ID.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Scope:</span>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="text-sm border-slate-300 rounded-md px-2 py-1"
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {data && (
              <span className="text-sm text-slate-500 ml-1">
                {data.count} {status === 'held' ? 'held' : 'total'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* CR processing gates — host-intake Save + per-operator Quote toggles */}
      <CrGatesControl />

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {isError && <div className="text-rose-700">Error: {error.message}</div>}

      {!isLoading && !isError && requests.length === 0 && (
        <div className="card p-8 text-center text-slate-500">
          <div className="text-2xl mb-1">⏸</div>
          {status === 'held'
            ? 'No held Centralized Requests. Everything the intake agent received was actionable.'
            : 'No Centralized Requests in the window.'}
        </div>
      )}

      {requests.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase text-slate-500 tracking-wider">
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Hold</th>
                  <th className="px-3 py-2">Route</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Sentinel Quote</th>
                  <th className="px-3 py-2">Incoming Request ID</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={`${r.trip_id}:${r.operator_id}`} className="border-b last:border-0 hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 max-w-xs">
                      <div className="text-slate-800">{r.subject || <span className="text-slate-400">— no subject —</span>}</div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge badge={r.badge} /></td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.departure_airport}→{r.arrival_airport}
                      {Array.isArray(r.legs) && r.legs.length > 1 && (
                        <span className="ml-1 text-[10px] text-slate-400">({r.legs.length} legs)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.departure_date}</td>
                    <td className="px-3 py-2 text-xs"><Customer name={r.customer_name} email={r.customer_email} /></td>
                    <td className="px-3 py-2"><SentinelQuote operator={r.operator_id} id={r.sentinel_quote_id} url={r.sentinel_url} /></td>
                    <td className="px-3 py-2"><CopyableId id={r.incoming_request_id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {requests.map(r => (
              <div key={`${r.trip_id}:${r.operator_id}`} className="card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm text-slate-800 font-medium">
                    {r.subject || <span className="text-slate-400">— no subject —</span>}
                  </div>
                  <StatusBadge badge={r.badge} />
                </div>
                <div className="mt-2 text-sm font-mono">{r.departure_airport}→{r.arrival_airport}
                  {Array.isArray(r.legs) && r.legs.length > 1 && ` · ${r.legs.length} legs`}
                </div>
                <div className="text-xs text-slate-500">{r.departure_date}</div>
                <div className="mt-1 text-xs"><Customer name={r.customer_name} email={r.customer_email} /></div>
                {r.sentinel_quote_id && (
                  <div className="mt-2">
                    <div className="text-xs text-slate-400">Sentinel Quote</div>
                    <SentinelQuote operator={r.operator_id} id={r.sentinel_quote_id} url={r.sentinel_url} />
                  </div>
                )}
                <div className="mt-2 text-xs text-slate-400">Incoming Request ID</div>
                <CopyableId id={r.incoming_request_id} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
